import { useState, type CSSProperties } from 'react';
import { pictureUrl } from '../api/catalog';
import { colourOf, defaultColour, parseSymbol, symbolLabel, type SideKey } from './natoSymbols';

/**
 * One NATO map symbol, drawn in the browser.
 *
 * A mission made in Eden marks its map with the engine's own icons: `b_inf`,
 * `o_motor_inf`, `n_installation`, `u_unknown` and the plain `mil_*` family.
 * Those icons live in the game's data, which a web page cannot read, so the
 * same vocabulary is drawn here as APP-6 shapes: the frame says whose it is
 * (friendly a rectangle, hostile a diamond, neutral a square, unknown a
 * quatrefoil) and the glyph inside says what it is. What the editor picks is
 * stored as the engine's own class name and reaches `setMarkerType` unchanged,
 * so what a player sees in game is the real icon and what an editor sees here
 * is the same symbol.
 *
 * Anything the platform does not know how to draw falls back to a dot, so a
 * mod's own marker type still works.
 *
 * Since 2026-09-08 the game's own icon is asked for first, through
 * `/api/catalog/picture`, which asks the panel — the same endpoint the match
 * reports draw their weapons and vehicles from. The point is that the editor,
 * the session map and the after-action report show **the same picture**, and
 * that a mod's marker type gets its real icon rather than our approximation of
 * it. Everything below stays as the fallback, and it is not a rare one: a
 * stranger reading a session link is not signed in, the panel may be down, and
 * a marker type the installation has no texture for answers 404. The drawing
 * is what a reader sees then, exactly as before.
 */

/**
 * The frame of a framed symbol, on a 100×100 box. Friendly is the wide
 * rectangle, hostile the diamond, neutral the square and unknown the
 * quatrefoil, which is APP-6 and is what the engine's icons look like.
 */
function frame(side: SideKey) {
  switch (side) {
    case 'o':
      return <path d="M50 14 L86 50 L50 86 L14 50 Z" />;
    case 'n':
      return <rect x="18" y="18" width="64" height="64" />;
    case 'u':
      return (
        <path
          d="M34 30 A16 16 0 0 1 66 30 A16 16 0 0 1 66 70 A16 16 0 0 1 34 70 A16 16 0 0 1 34 30 Z"
          transform="rotate(45 50 50)"
        />
      );
    default:
      return <rect x="12" y="26" width="76" height="48" />;
  }
}

/** What goes inside the frame. Kept to strokes so it stays legible at 20 px. */
function glyph(role: string) {
  switch (role) {
    case 'inf':
      return (
        <>
          <line x1="26" y1="34" x2="74" y2="66" />
          <line x1="74" y1="34" x2="26" y2="66" />
        </>
      );
    case 'motor_inf':
      return (
        <>
          <line x1="26" y1="32" x2="74" y2="60" />
          <line x1="74" y1="32" x2="26" y2="60" />
          <circle cx="50" cy="70" r="7" />
        </>
      );
    case 'mech_inf':
      return (
        <>
          <ellipse cx="50" cy="50" rx="28" ry="16" />
          <line x1="30" y1="38" x2="70" y2="62" />
          <line x1="70" y1="38" x2="30" y2="62" />
        </>
      );
    case 'armor':
      return <ellipse cx="50" cy="50" rx="28" ry="16" />;
    case 'recon':
      return <line x1="26" y1="66" x2="74" y2="34" />;
    case 'art':
      return <circle cx="50" cy="50" r="9" className="fill" />;
    case 'mortar':
      return (
        <>
          <circle cx="50" cy="58" r="7" className="fill" />
          <line x1="50" y1="51" x2="50" y2="30" />
        </>
      );
    case 'antiair':
      return (
        <>
          <path d="M30 66 L50 34 L70 66" />
          <line x1="30" y1="66" x2="70" y2="66" />
        </>
      );
    case 'air':
      return <path d="M24 42 A26 20 0 0 1 76 42" />;
    case 'plane':
      return (
        <>
          <path d="M24 44 A26 20 0 0 1 76 44" />
          <line x1="50" y1="44" x2="50" y2="64" />
        </>
      );
    case 'uav':
      return (
        <>
          <path d="M26 46 A24 18 0 0 1 74 46" />
          <line x1="34" y1="62" x2="66" y2="62" />
        </>
      );
    case 'naval':
      return <path d="M24 46 L50 70 L76 46" />;
    case 'med':
      return (
        <>
          <line x1="50" y1="32" x2="50" y2="68" />
          <line x1="32" y1="50" x2="68" y2="50" />
        </>
      );
    case 'service':
      return <rect x="36" y="38" width="28" height="24" className="fill" />;
    case 'support':
      return (
        <text x="50" y="62" textAnchor="middle" className="letter">
          S
        </text>
      );
    case 'maint':
      return (
        <text x="50" y="62" textAnchor="middle" className="letter">
          M
        </text>
      );
    case 'hq':
      return (
        <>
          <line x1="20" y1="78" x2="20" y2="30" />
          <line x1="20" y1="30" x2="46" y2="30" />
        </>
      );
    case 'installation':
      return <rect x="34" y="34" width="32" height="32" className="fill" />;
    default:
      return (
        <text x="50" y="64" textAnchor="middle" className="letter">
          ?
        </text>
      );
  }
}

/** The plain marks, drawn without a frame. */
function plain(symbol: string) {
  switch (symbol) {
    case 'mil_objective':
      return (
        <>
          <circle cx="50" cy="50" r="30" />
          <circle cx="50" cy="50" r="8" className="fill" />
        </>
      );
    case 'mil_warning':
      return (
        <>
          <path d="M50 16 L86 80 L14 80 Z" />
          <line x1="50" y1="40" x2="50" y2="62" />
          <circle cx="50" cy="71" r="3.5" className="fill" />
        </>
      );
    case 'mil_destroy':
      return (
        <>
          <line x1="20" y1="20" x2="80" y2="80" />
          <line x1="80" y1="20" x2="20" y2="80" />
        </>
      );
    case 'mil_pickup':
      return (
        <>
          <path d="M50 22 L50 66" />
          <path d="M32 50 L50 68 L68 50" />
          <line x1="24" y1="80" x2="76" y2="80" />
        </>
      );
    case 'mil_ambush':
      return <path d="M18 76 L50 24 L82 76" />;
    case 'mil_defend':
      return <path d="M50 18 L82 32 L82 56 Q82 76 50 84 Q18 76 18 56 L18 32 Z" />;
    case 'mil_arrow':
      return <path d="M50 16 L78 78 L50 62 L22 78 Z" />;
    case 'mil_flag':
      return (
        <>
          <line x1="28" y1="84" x2="28" y2="16" />
          <path d="M28 20 L74 30 L28 44 Z" className="fill" />
        </>
      );
    case 'mil_box':
      return <rect x="24" y="24" width="52" height="52" />;
    case 'mil_triangle':
      return <path d="M50 20 L82 78 L18 78 Z" />;
    case 'mil_start':
      return <path d="M30 22 L78 50 L30 78 Z" className="fill" />;
    case 'mil_end':
      return <rect x="28" y="28" width="44" height="44" className="fill" />;
    case 'mil_unknown':
      return (
        <text x="50" y="72" textAnchor="middle" className="letter big">
          ?
        </text>
      );
    // A place the generator could build a night around (the map step): a plain
    // dot, bigger for a bigger place, and a ring round the dot for the one chosen.
    // The game's own `mil_objective` is a circle with a cross, which reads as
    // "forbidden" on a map of places to pick (the owner, 2026-10-12).
    // Four sizes, a capital's dot four times as wide as a hamlet's (web card 196).
    case 'place_dot_xs':
      return <circle cx="50" cy="50" r="12" className="fill" />;
    case 'place_dot_s':
      return <circle cx="50" cy="50" r="20" className="fill" />;
    case 'place_dot_m':
      return <circle cx="50" cy="50" r="32" className="fill" />;
    case 'place_dot_l':
      return <circle cx="50" cy="50" r="46" className="fill" />;
    case 'place_ring':
      return (
        <>
          <circle cx="50" cy="50" r="40" />
          <circle cx="50" cy="50" r="20" className="fill" />
        </>
      );
    default:
      return <circle cx="50" cy="50" r="14" className="fill" />;
  }
}

/**
 * One symbol. `size` is the drawn side in CSS pixels; the box is square so a
 * pin can be centred on its position whatever the symbol.
 */
export function NatoSymbol({
  symbol,
  colour,
  size = 26,
  title,
}: {
  symbol: string;
  colour?: string;
  size?: number;
  title?: string;
}) {
  const parsed = parseSymbol(symbol);
  const stroke = colourOf(colour ?? defaultColour(symbol));
  const [drawn, setDrawn] = useState(false);
  const label = title ?? symbolLabel(symbol);
  if (!drawn && !symbol.startsWith('place_') && /^[A-Za-z0-9_.-]{1,64}$/.test(symbol)) {
    return (
      <img
        className="nato game"
        src={pictureUrl('marker', symbol)}
        width={size}
        height={size}
        alt=""
        title={label}
        aria-hidden="true"
        loading="lazy"
        // 404, a panel that is not answering, a viewer with no session: draw it.
        onError={() => setDrawn(true)}
      />
    );
  }
  return (
    <svg
      className="nato"
      viewBox="0 0 100 100"
      width={size}
      height={size}
      role="img"
      aria-label={label}
      style={{ '--nato-colour': stroke } as CSSProperties}
    >
      <title>{label}</title>
      <g className="halo">{parsed ? frame(parsed.side) : plain(symbol)}</g>
      <g className="ink">
        {parsed ? (
          <>
            {frame(parsed.side)}
            {glyph(parsed.role)}
          </>
        ) : (
          plain(symbol)
        )}
      </g>
    </svg>
  );
}
